import LandingHeadline from "../common/LandingHeadline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Bot,
  Braces,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gauge,
  LayoutDashboard,
  Maximize2,
  ScanSearch,
  ShieldCheck,
  X,
} from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import { BreadcrumbSchema, Button } from "../common/ui";
import { getLenis } from "../common/SmoothScroll";
import testCasesShot from "../../../assets/product/test-cases.webp";
import performanceShot from "../../../assets/product/performance-create-script.webp";
import apiShot from "../../../assets/product/api-testing-create.webp";

// Back to front: the deck fans out further on hover.
const cards = [
  { label: "API testing", src: apiShot, alt: "API testing in Froth TestOps", width: 1800, height: 800 },
  {
    label: "Performance",
    src: performanceShot,
    alt: "Performance test script in Froth TestOps",
    width: 1800,
    height: 925,
  },
  { label: "Test cases", src: testCasesShot, alt: "Test case repository in Froth TestOps", width: 1800, height: 700 },
];

// The capabilities the lead names.
const capabilities = [
  { label: "Test management", icon: LayoutDashboard },
  { label: "Automation", icon: Bot },
  { label: "API", icon: Braces },
  { label: "Performance", icon: Gauge },
  { label: "Security", icon: ShieldCheck },
  { label: "Code Analyzer", icon: ScanSearch },
];

// Full-size view of one deck card, with arrows to step through all of them.
function Lightbox({ index, onChange, onClose }) {
  const count = cards.length;
  const { label, src, alt, width, height } = cards[index];
  const step = delta => onChange((index + delta + count) % count);

  // Lock the page behind the viewer, including the site-wide smooth scroll.
  useEffect(() => {
    const { overflow } = document.body.style;
    const lenis = getLenis();
    document.body.style.overflow = "hidden";
    if (lenis) lenis.stop();
    return () => {
      document.body.style.overflow = overflow;
      if (lenis) lenis.start();
    };
  }, []);

  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange((index + 1) % count);
      if (e.key === "ArrowLeft") onChange((index - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onChange, onClose]);

  return createPortal(
    <div className="why-lightbox" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="why-lightbox__panel" onClick={e => e.stopPropagation()}>
        <div className="why-lightbox__head">
          <strong>{label}</strong>
          <span>
            {index + 1} / {count}
          </span>
          <button type="button" className="why-lightbox__close" onClick={onClose} aria-label="Close" autoFocus>
            <X size={20} />
          </button>
        </div>
        <img key={src} className="why-lightbox__img" src={src} alt={alt} width={width} height={height} />
        <div className="why-lightbox__thumbs">
          {cards.map((card, i) => (
            <button
              type="button"
              key={card.label}
              className={`why-lightbox__thumb${i === index ? " is-active" : ""}`}
              onClick={() => onChange(i)}
              aria-label={`Show ${card.label}`}
              aria-current={i === index}
            >
              <img src={card.src} alt="" />
              <span>{card.label}</span>
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="why-lightbox__nav why-lightbox__nav--prev"
        onClick={e => {
          e.stopPropagation();
          step(-1);
        }}
        aria-label="Previous screenshot"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        type="button"
        className="why-lightbox__nav why-lightbox__nav--next"
        onClick={e => {
          e.stopPropagation();
          step(1);
        }}
        aria-label="Next screenshot"
      >
        <ChevronRight size={24} />
      </button>
    </div>,
    document.body
  );
}

export default function WhyHero() {
  const [open, setOpen] = useState(null);

  return (
    <section className="why-hero">
      <BreadcrumbSchema name="Why TestOps" />
      <div className="ft-container why-hero__grid">
        <div className="why-hero__copy">
          <nav className="why-hero__crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Why TestOps</span>
          </nav>
          <span className="why-hero__eyebrow">
            <span className="why-hero__eyebrow-icon">
              <FrothAiIcon size={14} />
            </span>
            Why Froth TestOps
          </span>
          <LandingHeadline className="why-hero__title">
            Innovate, Automate, Elevate:{" "}
            <span className="why-hero__grad">
              Redefining <span className="why-hero__nowrap">AI-Driven</span> TestOps
            </span>
          </LandingHeadline>
          <p className="why-hero__statement">Froth TestOps turns testing into a business accelerator.</p>
          <p className="why-hero__lead">
            It combines test management, automation, API, performance and security testing in one platform, and FrothAI
            speeds up every step, from writing requirements and test cases to generating automation scripts and
            assessing release readiness. Whether your team runs manual or automated tests, Froth TestOps gives you one
            place to plan, execute, track and sign off quality.
          </p>
          <div className="ft-btn-row">
            <Button to="/contact" icon>
              Talk to Us
            </Button>
            <Button to="/froth-ai" variant="ghost">
              Explore FrothAI
            </Button>
          </div>
          <ul className="why-hero__caps" aria-label="In one platform">
            {capabilities.map(({ label, icon: Icon }) => (
              <li key={label} className="why-hero__cap">
                <Icon size={13} aria-hidden="true" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="why-hero__stage">
          <span className="why-hero__glow" aria-hidden="true" />
          <div className="why-hero__deck">
            {cards.map(({ label, src, alt, width, height }, i) => (
              <button
                type="button"
                key={label}
                className={`why-hero__card why-hero__card--${i + 1}`}
                onClick={() => setOpen(i)}
                aria-label={`Enlarge ${label} screenshot`}
              >
                <span className="why-hero__card-bar">
                  <span className="why-hero__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  {label}
                  <Maximize2 className="why-hero__zoom" size={14} aria-hidden="true" />
                </span>
                <img
                  src={src}
                  alt={alt}
                  width={width}
                  height={height}
                  fetchPriority={i === cards.length - 1 ? "high" : undefined}
                />
              </button>
            ))}
          </div>
          {open !== null && <Lightbox index={open} onChange={setOpen} onClose={() => setOpen(null)} />}

          <span className="why-hero__badge why-hero__badge--ai" aria-hidden="true">
            <span className="why-hero__badge-icon">
              <FrothAiIcon size={18} />
            </span>
            <span>
              <strong>FrothAI</strong>
              <small>Generating automation scripts</small>
            </span>
          </span>
          <span className="why-hero__badge why-hero__badge--ready" aria-hidden="true">
            <CheckCircle2 size={18} />
            Release ready
          </span>
        </div>
      </div>
    </section>
  );
}
